/**
 * tags.ts — the tag rules api/ and web/ must apply to the same text at the same moment.
 *
 * WHY THESE MOVED HERE. Tags are the one field a builder types FREELY and the server then RESOLVES:
 * 'PowerAutomate', 'power automate', 'power_automate' and 'Power-Automate' are all the same tag
 * (CAT-F11), decided by `normaliseTag` below. Until now that function lived only in
 * `api/src/db/tag.ts`, which meant the browser could not tell whether the word being typed was one the
 * platform already has. The consequence is the whole of CAT-F12: the form offered nothing, so the
 * builder invented a synonym, and the owner merged it away afterwards by hand.
 *
 * Same reasoning as search.ts — a rule that both sides apply, defined once. The drift this prevents is
 * not hypothetical: `api/test/admin/tool-governance.test.ts` carried a hand-copied `normaliseTag` that
 * collapsed whitespace to a SPACE instead of a hyphen, so it had been asserting against a normaliser
 * the product does not have.
 */
/**
 * CAT-F11 normalisation — THE one implementation, now genuinely one.
 *
 * Defined verbatim from migration 0003's comment block:
 *   lower(trim(label)) -> replace [\s_]+ with '-' -> strip non-[a-z0-9-] -> collapse repeated '-'
 *   -> trim leading/trailing '-'.
 *
 * The UNIQUE index on `lower(app.tag.normalised)` is what makes this binding rather than advisory: two
 * labels with the same output cannot both exist as tags. Pure and side-effect free. MAY RETURN '' for
 * an all-punctuation label — every caller has to handle that, because '' is not a key that can be
 * looked up or inserted.
 */
export function normaliseTag(label) {
    return label
        .trim()
        .toLowerCase()
        .replace(/[\s_]+/g, '-') // whitespace/underscore runs become a single hyphen
        .replace(/[^a-z0-9-]/g, '') // strip anything not a lowercase alnum or hyphen
        .replace(/-+/g, '-') // collapse repeated hyphens
        .replace(/^-|-$/g, ''); // trim leading/trailing hyphens left by the strip
}
/**
 * The tag bounds, in the one place both tiers can read them.
 *
 * `MAX_TAGS_PER_TOOL` is not a preference — it is `app.forbid_sixth_tag`, the BEFORE INSERT trigger in
 * migration 0004, which raises 'CAT-F12: a tool may have at most 5 tags'. `MAX_TAG_LABEL_LENGTH`
 * matches `app.tag.label`'s own bound. They were previously written as bare `.max(5)` / `.max(60)`
 * literals in three separate schemas plus the web form's LIMITS, none of which mentioned the trigger —
 * so a change to the trigger had four call sites to find, and missing one turns a friendly field error
 * into a 500 from a raised exception.
 */
export const MAX_TAGS_PER_TOOL = 5;
export const MAX_TAG_LABEL_LENGTH = 60;
/**
 * How many suggestions to put in front of the builder at once.
 *
 * SIX, not the whole vocabulary. There are 19 governed artefact-kind tags alone, and the field they
 * sit under is one row of a modal with the Next button below it — a list of 19 covers the control the
 * builder is trying to reach. Six is enough to fill a tool (the cap is 5) without scrolling, and the
 * list narrows as they type, which is what a type-ahead is for. Nothing is hidden permanently: a tag
 * outside the six is reachable by typing two more characters of it.
 */
export const MAX_TAG_SUGGESTIONS = 6;
/**
 * Which suggestions to show for what has been typed so far. Pure, so all three upload skins share one
 * answer and none of them re-implements the matching.
 *
 * MATCHES ON THE NORMALISED KEY, NOT ONLY THE LABEL. Typing 'power_auto' has to reach 'Power Automate'
 * — under a plain label substring it does not, because the label contains a space where the input has
 * an underscore. Normalising the query first makes the browser's idea of "close" the same as the
 * server's idea of "the same tag", which is the entire point of sharing this module.
 *
 * PREFIX MATCHES COME FIRST, and the server's usage order is preserved within each group (it sends the
 * list already ordered, busiest first). So typing 'flow' offers 'Flow' before 'Cloud flow' — what
 * somebody types is usually the start of the word they mean — while a mid-word match is still offered
 * rather than lost.
 *
 * ALREADY-CHOSEN TAGS ARE DROPPED BY NORMALISED KEY, not by label. A builder who has already added
 * 'power automate' must not be offered 'Power Automate': accepting it would send two labels that
 * resolve to one tag, which the server de-duplicates silently (`dedupeTags`), so the chip they clicked
 * would simply not appear on the finished tool.
 *
 * AN EXACT MATCH IS DROPPED TOO. Once the typed text IS a suggestion, the row adds nothing — pressing
 * Enter already produces exactly that tag — and leaving it there makes the panel look like it is
 * asking for a second confirmation.
 */
export function matchTagSuggestions(suggestions, typed, chosen, limit = MAX_TAG_SUGGESTIONS) {
    const taken = new Set(chosen.map(normaliseTag).filter((k) => k !== ''));
    const available = suggestions.filter((s) => !taken.has(s.normalised));
    const query = normaliseTag(typed);
    if (query === '')
        return available.slice(0, limit);
    const prefix = [];
    const contains = [];
    for (const s of available) {
        if (s.normalised === query)
            continue; // typing it exactly is already the same outcome
        if (s.normalised.startsWith(query))
            prefix.push(s);
        else if (s.normalised.includes(query))
            contains.push(s);
    }
    return [...prefix, ...contains].slice(0, limit);
}
/**
 * The short right-hand hint on one suggestion: WHY this word is worth reusing rather than inventing
 * another. Shared, not per-skin, because all three upload skins offer the same vocabulary and a hint
 * that said "Microsoft" in one and nothing in another would make the same tag look like two things.
 *
 * Usage comes first, because "9 tools" IS the argument — it says colleagues already search for this
 * word, which is the whole of CAT-F12. A platform artefact-kind tag with no tools yet is still worth
 * offering (it carries product provenance, CAT-F07), so it is labelled by what it is instead.
 *
 * A count of zero is never printed. "0 tools" reads as a reason NOT to pick it, which is the opposite
 * of what a freshly-installed, entirely-unused governed vocabulary needs.
 */
export function describeTagSuggestion(s) {
    if (s.toolCount > 0)
        return `${s.toolCount} ${s.toolCount === 1 ? 'tool' : 'tools'}`;
    return s.isSuggested ? 'Microsoft' : '';
}
//# sourceMappingURL=tags.js.map